import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

export interface PermisoItem {
  name: string;
  label: string;
}
export interface PermisoGrupo {
  key: string;
  label: string;
  permisos: PermisoItem[];
}
export interface Rol {
  id: number;
  name: string;
  protegido: boolean;
  users_count: number;
  permisos: string[];
}
export interface UsuarioConRoles {
  id: number;
  nombre: string;
  correo: string;
  roles: string[];
  permisos_directos: string[];
  permisos_roles: string[];
  permisos_efectivos: string[];
}
export interface MiembroEquipo {
  id: number;
  nombre: string;
  correo: string;
  rol: string;
  roles: string[];
  activo: boolean;
  creado_en: string | null;
  last_activity: string | null;
}

const BASE = "/settings";

export const settingsKeys = {
  all: ["settings"] as const,
  permissions: () => [...settingsKeys.all, "permissions"] as const,
  roles: () => [...settingsKeys.all, "roles"] as const,
  users: () => [...settingsKeys.all, "users"] as const,
  team: () => [...settingsKeys.all, "team"] as const,
};

export function usePermisos() {
  return useQuery({
    queryKey: settingsKeys.permissions(),
    queryFn: async () => {
      const { data } = await api.get<{ data: PermisoGrupo[] }>(`${BASE}/permissions`);
      return data.data;
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: settingsKeys.roles(),
    queryFn: async () => {
      const { data } = await api.get<{ data: Rol[] }>(`${BASE}/roles`);
      return data.data;
    },
  });
}

export function useCreateRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; permisos: string[] }) => {
      const { data } = await api.post(`${BASE}/roles`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

export function useUpdateRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: { name?: string; permisos?: string[] } }) => {
      const { data } = await api.patch(`${BASE}/roles/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

export function useDeleteRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

export function useUsuariosRoles() {
  return useQuery({
    queryKey: settingsKeys.users(),
    queryFn: async () => {
      const { data } = await api.get<{ data: UsuarioConRoles[] }>(`${BASE}/users`);
      return data.data;
    },
  });
}

export function useSyncUsuarioRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, roles }: { id: number; roles: string[] }) => {
      const { data } = await api.post(`${BASE}/users/${id}/roles`, { roles });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}


export function useSyncUsuarioPermisos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, permisos }: { id: number; permisos: string[] }) => {
      const { data } = await api.post(`${BASE}/users/${id}/permissions`, { permisos });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

// ── Equipo / Usuarios (gestión de cuentas) ──────────────────────────────────

export function useEquipo() {
  return useQuery({
    queryKey: settingsKeys.team(),
    queryFn: async () => {
      const { data } = await api.get<{ data: MiembroEquipo[] }>(`${BASE}/team`);
      return data.data;
    },
  });
}

export interface NuevoMiembro {
  nombre: string;
  correo: string;
  password: string;
  rol: string;
  activo?: boolean;
}

export function useCreateMiembro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NuevoMiembro) => {
      const { data } = await api.post(`${BASE}/team`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

export function useUpdateMiembro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<{ nombre: string; correo: string; rol: string; activo: boolean }>;
    }) => {
      const { data } = await api.patch(`${BASE}/team/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

export function useResetMiembroPassword() {
  return useMutation({
    mutationFn: async ({ id, password }: { id: number; password: string }) => {
      const { data } = await api.post(`${BASE}/team/${id}/password`, { password });
      return data;
    },
  });
}

export function useDeleteMiembro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/team/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

// ── Marca / White-label de la instalación ───────────────────────────────────

export interface AppSettings {
  plataforma_nombre: string;
  plataforma_eslogan: string;
  plataforma_logo: string | null;
  plataforma_logo_url: string | null;
  locale: string;
  moneda: string;
  formato_fecha: string;
}

export function useAppSettings() {
  return useQuery({
    queryKey: [...settingsKeys.all, "app"] as const,
    queryFn: async () => {
      const { data } = await api.get<{ data: AppSettings }>(`${BASE}/app`);
      return data.data;
    },
  });
}

export function useUpdateAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<AppSettings>) => {
      const { data } = await api.patch(`${BASE}/app`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.all }),
  });
}

export async function uploadAppLogo(file: File): Promise<{ path: string; url: string }> {
  const fd = new FormData();
  fd.append("logo", file);
  const { data } = await api.post<{ path: string; url: string }>(`${BASE}/app/logo`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function removeAppLogo(): Promise<void> {
  await api.delete(`${BASE}/app/logo`);
}
